import toast from 'react-hot-toast';
import type { Middleware } from '@reduxjs/toolkit';

// Map action types to success messages (mutations only — fetches are excluded)
const SUCCESS_MESSAGES: Record<string, string> = {
  // Menu selections
  'weeklyMenu/createSelection/fulfilled': 'Meal selected!',
  'weeklyMenu/updateSelection/fulfilled': 'Selection updated!',
  'weeklyMenu/deleteSelection/fulfilled': 'Selection removed!',
  'weeklyMenu/skipMeal/fulfilled': 'Meal skipped!',
  // Auth
  'auth/login/fulfilled': 'Welcome back!',
  'auth/signup/fulfilled': 'Account created! Please verify your email.',
  'auth/logout/fulfilled': 'Logged out.',
  // Profile
  'userProfile/updateProfile/fulfilled': 'Profile updated!',
  // Addresses
  'address/createAddress/fulfilled': 'Address saved!',
  'address/updateAddress/fulfilled': 'Address updated!',
  'address/deleteAddress/fulfilled': 'Address deleted!',
  // Orders
  'orders/cancelOrder/fulfilled': 'Order cancelled.',
  'orders/rateOrder/fulfilled': 'Rating submitted!',
  // Health
  'health/logWeight/fulfilled': 'Weight logged!',
  'health/logWater/fulfilled': 'Water logged!',
  'health/addHealthData/fulfilled': 'Health data saved!',
  // Wallet
  'wallet/addFunds/fulfilled': 'Funds added!',
  'wallet/redeemReward/fulfilled': 'Reward redeemed!',
  // Meals
  'meals/addMeal/fulfilled': 'Meal logged!',
  'meals/deleteMeal/fulfilled': 'Meal removed!',
  'meals/updateMeal/fulfilled': 'Meal updated!',
  // Workouts
  'workouts/createWorkoutPlan/fulfilled': 'Workout plan created!',
  'workouts/updateWorkoutPlan/fulfilled': 'Workout plan updated!',
  'workouts/deleteWorkoutPlan/fulfilled': 'Workout plan deleted!',
  'workouts/logWorkout/fulfilled': 'Workout logged!',
  // Social
  'social/createPost/fulfilled': 'Post published!',
  'social/deletePost/fulfilled': 'Post deleted!',
  'social/likePost/fulfilled': 'Liked!',
  // Admin
  'admin/updateOrder/fulfilled': 'Order updated!',
  'admin/updateUser/fulfilled': 'User updated!',
  'admin/deleteRecipe/fulfilled': 'Recipe deleted!',
  'admin/createRecipe/fulfilled': 'Recipe created!',
};

// Noise actions to suppress error toasts (background polls, optional fetches)
const SILENT_REJECTIONS = new Set([
  'weeklyMenu/fetchRecommendations/rejected',
  'assistant/fetchConversation/rejected',
  'notifications/fetchNotifications/rejected',
  'gamification/fetchAchievements/rejected',
  'gamification/fetchChallenges/rejected',
  'gamification/fetchLeaderboard/rejected',
]);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const toastMiddleware: Middleware = () => (next) => (action: any) => {
  const result = next(action);
  const type: string = action?.type ?? '';

  if (type.endsWith('/rejected') && !SILENT_REJECTIONS.has(type)) {
    const msg =
      typeof action.payload === 'string'
        ? action.payload
        : action.error?.message || 'Something went wrong. Please try again.';
    if (msg) toast.error(msg, { id: type });
  }

  if (type.endsWith('/fulfilled') && SUCCESS_MESSAGES[type]) {
    toast.success(SUCCESS_MESSAGES[type], { id: type });
  }

  return result;
};
